/* eslint-disable @typescript-eslint/no-explicit-any, no-empty */
/**
 * Timeline Store (Local-Only)
 * 
 * CRITICAL: Timeline data is intentionally kept local and NEVER synced to Firestore.
 * This is by design to preserve user privacy and ensure the timeclock override
 * (live vs manual mode) remains device-local and not cloud-persisted.
 * 
 * - No Firestore reads or writes
 * - No Firebase SDK imports
 * - All data stored in localStorage only
 * - Local in-memory listener registry for reactive updates
 * 
 * Keep API surface so the feature can be re-enabled later if desired.
 */

export type TimelineMode = "live" | "manual";

export type TimelineData = {
  mode: TimelineMode;
  timestamp?: string;
};

const TIMELINE_DOC_PREFIX = "timeline-";
const LOCAL_TIMELINE_KEY_PREFIX = "timeline-data:";

export const getTimelineDocId = (viewId: string): string => `${TIMELINE_DOC_PREFIX}${viewId}`;

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs = 4000): Promise<T> =>
  new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error("Firestore request timed out."));
    }, timeoutMs);

    promise
      .then((result) => {
        clearTimeout(timeoutId);
        resolve(result);
      })
      .catch((error) => {
        clearTimeout(timeoutId);
        reject(error);
      });
  });

// Local-only listeners so timeline changes propagate without Firestore
const timelineListeners = new Map<string, Set<(data: TimelineData | null) => void>>();

const emitTimeline = (timelineDocId: string, data: TimelineData | null) => {
  const listeners = timelineListeners.get(timelineDocId);
  if (!listeners) return;
  listeners.forEach((cb) => {
    try {
      cb(data);
    } catch {}
  });
};

// Firestore disabled: no-op snapshot getter. Kept for API parity.
const getTimelineSnapshot = async (_timelineDocId: string) => {
  throw new Error("Firestore timeline snapshot is disabled in this build.");
};

const loadLocalTimeline = (storageKey: string): TimelineData | null => {
  const stored = localStorage.getItem(`${LOCAL_TIMELINE_KEY_PREFIX}${storageKey}`);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as TimelineData;
    return {
      mode: parsed.mode ?? "live",
      timestamp: parsed.timestamp,
    };
  } catch {
    return null;
  }
};

const saveLocalTimeline = (storageKey: string, data: TimelineData) => {
  // Never include updatedAt; store only mode/timestamp per design.
  localStorage.setItem(`${LOCAL_TIMELINE_KEY_PREFIX}${storageKey}`, JSON.stringify({ mode: data.mode, timestamp: data.timestamp }));
};

export async function loadTimelineData(timelineDocId: string): Promise<TimelineData | null> {
  // Always load from localStorage only. This prevents any network activity.
  return loadLocalTimeline(timelineDocId);
}

export async function saveTimelineData(timelineDocId: string, data: TimelineData): Promise<void> {
  // diagnostics removed

  // Local-only: always allow; no network writes and no interaction guard needed.

  // Always persist locally only to avoid network writes. Keep same event
  // semantics so callers receive the same notifications.
  try {
    saveLocalTimeline(timelineDocId, {
      mode: data.mode,
      timestamp: data.timestamp,
    });
    emitTimeline(timelineDocId, data);
    // diagnostics removed
    } catch (err) {
    try {
      console.error('[timelineStore] saveTimelineData failed (local)', err);
    } catch {}
  }
}

// Allow the next save for a given timelineDocId for a short time window (one user interaction).
export function allowTimelineSaveOnce(timelineDocId: string, windowMs = 5000) {
  try {
    (saveTimelineData as any)._allowed = (saveTimelineData as any)._allowed ?? new Map<string, number>();
    const allowedMap = (saveTimelineData as any)._allowed as Map<string, number>;
    allowedMap.set(timelineDocId, Date.now() + windowMs);
    // Also emit a diagnostic event
    // diagnostics removed
  } catch {}
}

export function subscribeTimelineData(
  timelineDocId: string,
  onData: (data: TimelineData | null) => void,
  onError?: (error: Error) => void
): (() => void) | null {
  // Subscriptions are local-only. We keep an in-memory listener registry
  // so widgets can react to timeline changes without any network traffic.
  try {
    onData(loadLocalTimeline(timelineDocId));
  } catch (err) {
    try {
      onError?.(err as Error);
    } catch {}
  }

  const listeners = timelineListeners.get(timelineDocId) ?? new Set<(data: TimelineData | null) => void>();
  listeners.add(onData);
  timelineListeners.set(timelineDocId, listeners);
  const storageKey = `${LOCAL_TIMELINE_KEY_PREFIX}${timelineDocId}`;
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== storageKey) return;
    try {
      onData(loadLocalTimeline(timelineDocId));
    } catch (err) {
      try {
        onError?.(err as Error);
      } catch {}
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("storage", handleStorage);
  }

  return () => {
    const current = timelineListeners.get(timelineDocId);
    if (!current) return;
    current.delete(onData);
    if (current.size === 0) {
      timelineListeners.delete(timelineDocId);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", handleStorage);
    }
  };
}
